import { useState, useEffect, useRef } from 'react';
import { FileText, UploadCloud, Download, Trash2, Loader2, File, Shield, ShieldCheck, ExternalLink, Link2, Plus, X, Tag } from 'lucide-react';
import { getCompanyMaterials, uploadCompanyMaterial, deleteCompanyMaterial, markAsCompanyProfile, unmarkCompanyProfile, addCompanyMaterialLink, updateCompanyMaterial } from '../../services/salesMaterialsService';
import { toLocaleDateStringInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import type { CompanyMaterial, CompanyMaterialCategory } from '../../types/settings';
import { MATERIAL_CATEGORY_LABELS } from '../../types/settings';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';

const CATEGORY_OPTIONS = Object.entries(MATERIAL_CATEGORY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export function CompanyMaterialsSettings() {
  const tz = useTimezone();
  const [materials, setMaterials] = useState<CompanyMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [markingProfile, setMarkingProfile] = useState<string | null>(null);
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkCategory, setLinkCategory] = useState<CompanyMaterialCategory | ''>('');
  const [linkDescription, setLinkDescription] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    setLoading(true);
    const data = await getCompanyMaterials();
    setMaterials(data);
    setLoading(false);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/png',
      'image/jpeg',
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload PDF, DOCX, PPTX, PNG, or JPG files.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    const material = await uploadCompanyMaterial(file);

    if (material) {
      setMaterials([...materials, material]);
    }

    setUploading(false);
  };

  const handleAddLink = async () => {
    if (!linkName.trim() || !linkUrl.trim() || !linkCategory || !linkDescription.trim()) return;

    try {
      new URL(linkUrl);
    } catch {
      alert('Please enter a valid URL');
      return;
    }

    setAddingLink(true);
    const material = await addCompanyMaterialLink(
      linkName.trim(),
      linkUrl.trim(),
      linkCategory || undefined,
      linkDescription.trim() || undefined
    );

    if (material) {
      setMaterials([...materials, material]);
      setLinkName('');
      setLinkUrl('');
      setLinkCategory('');
      setLinkDescription('');
      setShowAddLink(false);
    }
    setAddingLink(false);
  };

  const handleUpdateCategory = async (materialId: string, category: CompanyMaterialCategory | '') => {
    const success = await updateCompanyMaterial(materialId, {
      category: category || undefined,
    });
    if (success) {
      setMaterials(materials.map(m =>
        m.id === materialId ? { ...m, category: category || undefined } : m
      ));
    }
  };

  const handleUpdateDescription = async (materialId: string, description: string) => {
    const success = await updateCompanyMaterial(materialId, {
      description: description.trim() || undefined,
    });
    if (success) {
      setMaterials(materials.map(m =>
        m.id === materialId ? { ...m, description: description.trim() || undefined } : m
      ));
    }
    setEditingMaterial(null);
  };

  const handleDelete = async (materialId: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    const success = await deleteCompanyMaterial(materialId);

    if (success) {
      const deleted = materials.find(m => m.id === materialId);
      if (deleted?.is_company_profile) {
        setProfileUrl(null);
      }
      setMaterials(materials.filter(m => m.id !== materialId));
    }
  };

  const handleMarkProfile = async (materialId: string) => {
    const material = materials.find(m => m.id === materialId);
    if (!material) return;

    if (material.is_company_profile) {
      setMarkingProfile(materialId);
      const success = await unmarkCompanyProfile();
      if (success) {
        setMaterials(materials.map(m => ({ ...m, is_company_profile: false })));
        setProfileUrl(null);
      }
      setMarkingProfile(null);
      return;
    }

    setMarkingProfile(materialId);
    const result = await markAsCompanyProfile(materialId);
    if (result.success) {
      setMaterials(materials.map(m => ({
        ...m,
        is_company_profile: m.id === materialId,
      })));
      if (result.profileUrl) {
        setProfileUrl(result.profileUrl);
      }
    }
    setMarkingProfile(null);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (material: CompanyMaterial) => {
    if (material.source === 'link' || material.type === 'link') return Link2;
    if (material.type.includes('pdf')) return FileText;
    return File;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
      </div>
    );
  }

  const currentProfile = materials.find(m => m.is_company_profile);

  return (
    <div className="max-w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display mb-2">Company Materials</h2>
        <p className="text-gray-600">
          Upload files or add links that AI agents can share with prospects (recordings, guides, videos, etc.)
        </p>
      </div>

      <div className="space-y-6">
        {/* Upload & Add Link actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`bg-gradient-to-br from-white to-[#1875FF]/5 border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
              dragActive
                ? 'border-[#05E0D1] bg-[#1875FF]/5 shadow-2xl scale-[1.01]'
                : 'border-[#1875FF]/30 hover:border-[#05E0D1] hover:shadow-xl hover:-translate-y-1'
            }`}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full">
                <UploadCloud className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  {uploading ? 'Uploading...' : 'Upload a file'}
                </p>
                <p className="text-xs text-gray-500">
                  PDF, DOCX, PPTX, PNG, JPG (max 10MB)
                </p>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-5 py-2.5 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 font-semibold text-sm"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </span>
                ) : (
                  'Browse Files'
                )}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.pptx,.png,.jpg,.jpeg"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
          </div>

          <div
            onClick={() => setShowAddLink(true)}
            className="bg-gradient-to-br from-white to-emerald-50 border-2 border-dashed border-emerald-200 rounded-xl p-8 text-center transition-all duration-300 hover:border-emerald-400 hover:shadow-xl hover:-translate-y-1 cursor-pointer"
          >
            <div className="flex flex-col items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-full">
                <Link2 className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Add a link
                </p>
                <p className="text-xs text-gray-500">
                  YouTube, Drive, any URL
                </p>
              </div>
              <span className="px-5 py-2.5 bg-gradient-to-br from-emerald-500 to-teal-500 text-white rounded-xl font-semibold text-sm inline-flex items-center gap-1.5">
                <Plus className="w-4 h-4" />
                Add Link
              </span>
            </div>
          </div>
        </div>

        {/* Add Link Modal */}
        {showAddLink && (
          <div className="bg-white rounded-xl border-2 border-emerald-200 shadow-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 font-display flex items-center gap-2">
                <Link2 className="w-5 h-5 text-emerald-500" />
                Add Link
              </h3>
              <button
                onClick={() => { setShowAddLink(false); setLinkName(''); setLinkUrl(''); setLinkCategory(''); setLinkDescription(''); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
                <input
                  type="text"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  placeholder="e.g. AI Cold Call Recording"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">URL *</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Category *</label>
                <SimpleCustomSelect
                  value={linkCategory}
                  onChange={(v) => setLinkCategory(v as CompanyMaterialCategory | '')}
                  options={CATEGORY_OPTIONS}
                  placeholder="Select category..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description *</label>
                <input
                  type="text"
                  value={linkDescription}
                  onChange={(e) => setLinkDescription(e.target.value)}
                  placeholder="What is this asset? How should agents use it?"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleAddLink}
                disabled={addingLink || !linkName.trim() || !linkUrl.trim() || !linkCategory || !linkDescription.trim()}
                className="px-5 py-2.5 bg-gradient-to-br from-emerald-500 to-teal-500 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 font-semibold text-sm inline-flex items-center gap-2"
              >
                {addingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add
              </button>
            </div>
          </div>
        )}

        {/* Materials List */}
        {materials.length > 0 && (
          <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-bold text-gray-900 font-display">
                Materials ({materials.length})
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                AI agents will use these assets to share with prospects when relevant
              </p>
            </div>
            <div className="p-4 space-y-3">
              {materials.map((material) => {
                const Icon = getFileIcon(material);
                const isProfile = material.is_company_profile;
                const isMarking = markingProfile === material.id;
                const isLink = material.source === 'link' || material.type === 'link';
                const isEditing = editingMaterial === material.id;
                return (
                  <div
                    key={material.id}
                    className={`group rounded-lg p-4 transition-all duration-200 border ${
                      isProfile
                        ? 'bg-emerald-50/60 border-emerald-200 shadow-sm'
                        : 'bg-gray-50 border-transparent hover:border-gray-200 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className={`p-3 rounded-lg ${
                          isLink
                            ? 'bg-gradient-to-br from-emerald-100 to-teal-50'
                            : isProfile
                              ? 'bg-gradient-to-br from-emerald-100 to-emerald-50'
                              : 'bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10'
                        }`}>
                          <Icon className={`w-6 h-6 ${isProfile ? 'text-emerald-600' : isLink ? 'text-emerald-500' : ''}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {material.name}
                            </p>
                            {isProfile && (
                              <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                                <ShieldCheck className="w-3 h-3" />
                                Company Profile
                              </span>
                            )}
                            {material.category && (
                              <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                <Tag className="w-3 h-3" />
                                {MATERIAL_CATEGORY_LABELS[material.category]}
                              </span>
                            )}
                            {isLink && (
                              <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                <Link2 className="w-3 h-3" />
                                Link
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {isLink ? (
                              <span className="text-blue-600 truncate block max-w-xs">{material.url}</span>
                            ) : (
                              <>
                                Uploaded on {toLocaleDateStringInTz(material.uploaded_at, tz, 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                {formatFileSize(material.size) && <> &bull; {formatFileSize(material.size)}</>}
                              </>
                            )}
                          </p>
                          {material.description && !isEditing && (
                            <p className="text-xs text-gray-600 mt-1 italic">{material.description}</p>
                          )}
                          {isProfile && profileUrl && (
                            <a
                              href={profileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 mt-1 font-medium"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Shareable link
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        {!isLink && (
                          <button
                            onClick={() => handleMarkProfile(material.id)}
                            disabled={isMarking}
                            className={`p-2.5 rounded-lg transition-colors disabled:opacity-50 ${
                              isProfile
                                ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100'
                                : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                            }`}
                            title={isProfile ? 'Remove as Company Profile' : 'Set as Company Profile'}
                          >
                            {isMarking ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : isProfile ? (
                              <ShieldCheck className="w-5 h-5" />
                            ) : (
                              <Shield className="w-5 h-5" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => setEditingMaterial(isEditing ? null : material.id)}
                          className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit category / description"
                        >
                          <Tag className="w-5 h-5" />
                        </button>
                        <a
                          href={material.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2.5 text-gray-500 hover:text-[#1875FF] hover:bg-blue-50 rounded-lg transition-colors"
                          title={isLink ? 'Open link' : 'Download'}
                        >
                          {isLink ? <ExternalLink className="w-5 h-5" /> : <Download className="w-5 h-5" />}
                        </a>
                        <button
                          onClick={() => handleDelete(material.id)}
                          className="p-2.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Inline edit for category/description */}
                    {isEditing && (
                      <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                          <SimpleCustomSelect
                            value={material.category || ''}
                            onChange={(v) => handleUpdateCategory(material.id, v as CompanyMaterialCategory | '')}
                            options={CATEGORY_OPTIONS}
                            placeholder="No category"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                          <input
                            type="text"
                            defaultValue={material.description || ''}
                            onBlur={(e) => handleUpdateDescription(material.id, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateDescription(material.id, (e.target as HTMLInputElement).value); }}
                            placeholder="What is this? How should agents use it?"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {currentProfile && (
          <div className="bg-emerald-50/50 rounded-xl border border-emerald-200 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-sm text-emerald-800">
                <p className="font-semibold mb-1">Company Profile Active</p>
                <p className="text-emerald-700 text-xs leading-relaxed">
                  Your AI agents will share "{currentProfile.name}" with prospects when appropriate, using a clean shareable link instead of a raw file URL.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
